import type { KeyboardEvent } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import { taskDndId, stripHtml, EASYKIT_TAGS } from '@/utils/easykit';
import { useEasykitTaskDisplay } from './useEasykitTaskDisplay';
import type { OdooTask } from '@/types/odoo';

interface EasykitTaskCardProps {
  task: OdooTask;
  isSelected?: boolean;
}

const TAG_LABELS: Record<number, { label: string; cls: string }> = Object.fromEntries(
  EASYKIT_TAGS.map((t) => [t.id, { label: t.label, cls: t.card }]),
);

export const TYPE_COLORS = {
  druktest: { bg: 'bg-red-100', bgSelected: 'bg-red-300', border: 'border-l-red-600', text: 'text-red-800' },
  levering: { bg: 'bg-blue-50', bgSelected: 'bg-blue-200', border: 'border-l-blue-500', text: 'text-blue-700' },
  feestdag: { bg: 'bg-purple-50', bgSelected: 'bg-purple-200', border: 'border-l-purple-500', text: 'text-purple-700' },
  other: { bg: 'bg-gray-50', bgSelected: 'bg-gray-200', border: 'border-l-gray-400', text: 'text-gray-700' },
};

export function EasykitTaskCard({ task, isSelected }: EasykitTaskCardProps) {
  const selectTask = useEasykitPlanningStore((s) => s.selectTask);
  const saveInFlight = useEasykitPlanningStore((s) => s.saveInFlight);

  const {
    taskType, color, pill, primaryLabel,
    soName, projectName, address, prefix,
    isOnHold, isFeestdag,
  } = useEasykitTaskDisplay(task);
  // Feestdag (holidays project) and on-hold tasks aren't draggable —
  // they represent unavailability, not work that can be moved.
  const isImmovable = isOnHold || isFeestdag;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: taskDndId(task.id), disabled: isImmovable || saveInFlight });

  // Multiple-containers model: sibling shift is driven by the live drag layout
  // (committed array order), so the transform/transition here animate a STABLE
  // make-room slot — no per-frame oscillation. The dragged card itself is hidden
  // (opacity 0); the DragOverlay renders the floating clone that follows the cursor.
  const transformStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const rawDesc = typeof task.description === 'string' ? stripHtml(task.description) : '';

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      selectTask(task.id);
    }
  };

  return (
    // role + tabIndex are supplied by dnd-kit's `attributes` spread below.
    <div
      ref={setNodeRef}
      style={isOnHold ? {
        ...transformStyle,
        backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(180,83,9,0.12) 4px, rgba(180,83,9,0.12) 8px)',
        backgroundColor: '#fef3c7',
      } : transformStyle}
      className={`w-full text-left p-1.5 rounded border-l-3 ${isOnHold ? 'border-l-amber-600 opacity-60' : `${color.border} ${isSelected ? color.bgSelected : color.bg}`} hover:shadow-sm transition-shadow mb-1 last:mb-0 ${isImmovable ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'} touch-none ${isSelected ? 'shadow-md' : ''} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-td-blue/40`}
      {...attributes}
      {...listeners}
      onClick={() => selectTask(task.id)}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-center gap-1">
        {pill && (
          <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded ${pill.cls}`}>
            {pill.label}
          </span>
        )}
        {prefix && (
          <span className="shrink-0 text-[10px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded truncate max-w-[40%]">
            {prefix}
          </span>
        )}
        <p className={`text-xs font-bold truncate flex-1 ${isOnHold ? 'text-amber-700' : color.text}`}>
          {primaryLabel}
          {soName && <span className="ml-1 font-normal text-td-muted">· {soName}</span>}
        </p>
        {isOnHold && (
          <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-200 text-amber-800">ON HOLD</span>
        )}
      </div>
      {projectName && (
        <p className="text-[10px] text-td-muted truncate">{projectName}</p>
      )}
      {address && (
        <p className="text-[10px] text-td-light truncate">{address}</p>
      )}
      {(task.tag_ids ?? []).some((id) => TAG_LABELS[id]) && (
        <div className="flex items-center gap-1 mt-0.5">
          {(task.tag_ids ?? []).map((id) => {
            const tag = TAG_LABELS[id];
            return tag ? (
              <span key={id} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tag.cls}`}>
                {tag.label}
              </span>
            ) : null;
          })}
        </div>
      )}
      {rawDesc && (
        <p className={`text-[10px] text-purple-700 mt-0.5 ${isSelected ? 'whitespace-normal' : 'truncate'}`}>
          {rawDesc}
        </p>
      )}
    </div>
  );
}
